/**
 * StorySettingsPanel — slide-over inspector for a story's metadata: title,
 * description, genre, tags, cover accent, status, visibility, goal.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { cx } from '@/components/site/primitives';
import { GENRES, STATUS, VISIBILITY_META, COVER_TONES } from './constants';
import { getStoryWordCount, formatDate } from './utils/storyHelpers';
import StoryCover from './StoryCover';

function Field({ label, children }) {
  return (
    <div className="mt-5">
      <label className="text-xs font-semibold uppercase tracking-wide text-subtle">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function StorySettingsPanel({ story, isOpen, onClose, onUpdate }) {
  const [tagInput, setTagInput] = useState('');
  const wordCount = getStoryWordCount(story);

  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;
    if (!story.tags?.includes(value)) onUpdate({ tags: [...(story.tags ?? []), value] });
    setTagInput('');
  };

  const removeTag = (tag) => onUpdate({ tags: story.tags.filter((t) => t !== tag) });

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[92vw] max-w-sm overflow-y-auto border-line bg-card p-6 text-ink sm:max-w-sm" data-testid="my-writing-settings-panel">
        <SheetTitle className="font-display text-xl font-semibold text-ink">Story Settings</SheetTitle>

        <StoryCover story={story} className="mt-4 h-32 w-24" />

        <Field label="Title">
          <Input value={story.title} onChange={(e) => onUpdate({ title: e.target.value })} className="rounded-xl border-line bg-cream" />
        </Field>

        <Field label="Description">
          <Textarea value={story.description ?? ''} onChange={(e) => onUpdate({ description: e.target.value })} rows={3} className="rounded-xl border-line bg-cream text-sm" />
        </Field>

        <Field label="Genre">
          <Select value={story.genre} onValueChange={(v) => onUpdate({ genre: v })}>
            <SelectTrigger className="rounded-xl border-line bg-cream"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Tags">
          <div className="flex flex-wrap gap-1.5">
            {(story.tags ?? []).map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-cream px-2.5 py-1 text-[11px] font-medium text-ink">
                {tag}
                <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`} className="text-subtle hover:text-coral">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            onBlur={addTag}
            placeholder="Add a tag and press Enter"
            className="mt-2 rounded-xl border-line bg-cream text-sm"
          />
        </Field>

        <Field label="Cover accent">
          <div className="flex gap-2.5">
            {COVER_TONES.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-label={`${t.key} accent`}
                onClick={() => onUpdate({ cover: { ...story.cover, type: 'typographic', tone: t.key } })}
                className={cx(
                  'h-7 w-7 rounded-full ring-offset-2 ring-offset-card transition-all',
                  (story.cover?.tone ?? '') === t.key ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100'
                )}
                style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})` }}
              />
            ))}
          </div>
        </Field>

        <Field label="Status">
          <Select value={story.status} onValueChange={(v) => onUpdate({ status: v })}>
            <SelectTrigger className="rounded-xl border-line bg-cream"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.values(STATUS).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Visibility">
          <div className="flex flex-col gap-2">
            {Object.values(VISIBILITY_META).map((v) => (
              <button
                key={v.value}
                type="button"
                onClick={() => onUpdate({ visibility: v.value })}
                className={cx(
                  'rounded-xl border px-3 py-2 text-left transition-colors',
                  story.visibility === v.value ? 'border-coral bg-coral/10' : 'border-line hover:border-coral/40'
                )}
              >
                <p className="text-sm font-semibold text-ink">{v.label}</p>
                <p className="text-[11px] text-subtle">{v.hint}</p>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Writing goal">
          <Input
            type="number"
            min="0"
            value={story.goal ?? ''}
            onChange={(e) => onUpdate({ goal: e.target.value ? Number(e.target.value) : null })}
            placeholder="No goal set"
            className="rounded-xl border-line bg-cream"
          />
        </Field>

        <div className="mt-6 space-y-1 border-t border-line pt-4 text-xs text-subtle">
          <p>{wordCount.toLocaleString()} words · {story.chapters?.length ?? 0} chapters</p>
          <p>Created {formatDate(story.createdAt)}</p>
          <p>Last edited {formatDate(story.updatedAt)}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
